import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Trash2, Circle, Plus, X, Clock } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";

const DAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];

export default function ChatAdmin() {
  return (
    <div>
      <h1 className="text-3xl font-medium tracking-tight mb-6">Chat</h1>
      <Tabs defaultValue="gesprekken">
        <TabsList className="bg-cream-subtle rounded-full mb-6">
          <TabsTrigger value="gesprekken" data-testid="chat-tab-conversations" className="rounded-full">Gesprekken</TabsTrigger>
          <TabsTrigger value="instellingen" data-testid="chat-tab-settings" className="rounded-full">Chat-instellingen</TabsTrigger>
        </TabsList>
        <TabsContent value="gesprekken"><Conversations /></TabsContent>
        <TabsContent value="instellingen"><ChatSettings /></TabsContent>
      </Tabs>
    </div>
  );
}

function Conversations() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadSessions = useCallback(() => api.get("/admin/chat/sessions").then((r) => setSessions(r.data)).catch(() => {}), []);
  const loadMessages = useCallback((sid) => api.get(`/admin/chat/${sid}/messages`).then((r) => setMessages(r.data)).catch(() => {}), []);

  useEffect(() => { loadSessions(); const t = setInterval(loadSessions, 5000); return () => clearInterval(t); }, [loadSessions]);
  useEffect(() => {
    if (!active) return;
    loadMessages(active.id);
    const t = setInterval(() => loadMessages(active.id), 4000);
    return () => clearInterval(t);
  }, [active, loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    await api.post(`/admin/chat/${active.id}/message`, { text: t });
    loadMessages(active.id); loadSessions();
  };
  const del = async (s) => {
    if (!window.confirm("Gesprek verwijderen?")) return;
    await api.delete(`/admin/chat/${s.id}`);
    if (active?.id === s.id) setActive(null);
    loadSessions();
  };

  return (
    <div className="grid md:grid-cols-3 gap-4 h-[70vh]">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-[#F2EBE1] text-sm font-medium">Gesprekken ({sessions.length})</div>
        <div className="flex-1 overflow-auto divide-y divide-[#F2EBE1]">
          {!sessions.length && <div className="p-6 text-center text-ink-muted text-sm">Nog geen gesprekken.</div>}
          {sessions.map((s) => (
            <button key={s.id} data-testid={`chat-session-${s.id}`} onClick={() => setActive(s)}
              className={`w-full text-left px-4 py-3 hover:bg-cream-subtle/50 transition-colors ${active?.id === s.id ? "bg-brand-light/40" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{s.visitor_name || "Bezoeker"}</span>
                {s.unread_admin > 0 && <span className="bg-brand text-white text-[10px] rounded-full px-1.5 py-0.5" data-testid={`chat-unread-${s.id}`}>{s.unread_admin}</span>}
              </div>
              <div className="text-xs text-ink-muted truncate">{s.last_text || "—"}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 bg-white rounded-2xl border border-[#E8E2D9] flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-ink-muted text-sm"><MessageCircle className="w-5 h-5 mr-2" /> Kies een gesprek</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-[#F2EBE1] flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{active.visitor_name}</div>
                {active.visitor_email && <div className="text-xs text-ink-muted">{active.visitor_email}</div>}
              </div>
              <Button size="icon" variant="ghost" onClick={() => del(active)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3 bg-[#FAF7F2]">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                    m.sender === "admin" ? "bg-brand text-white" : m.sender === "system" ? "bg-[#EFEAE2] text-ink-muted italic" : "bg-white border border-[#E8E2D9]"}`}>
                    {m.text}
                    <div className={`text-[10px] mt-0.5 ${m.sender === "admin" ? "text-white/70" : "text-ink-muted"}`}>{format(parseISO(m.created_at), "HH:mm", { locale: nl })}</div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-[#F2EBE1] flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="chat-reply-input" placeholder="Typ een antwoord…" className="rounded-full border-[#E8E2D9]" />
              <Button onClick={send} data-testid="chat-send-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white shrink-0"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChatSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;

  const hours = s.chat_hours || [];
  const setHour = (day, patch) => setS({ ...s, chat_hours: hours.map((h) => h.day === day ? { ...h, ...patch } : h) });
  const qr = s.chat_quick_replies || [];
  const setQr = (i, patch) => setS({ ...s, chat_quick_replies: qr.map((q, idx) => idx === i ? { ...q, ...patch } : q) });
  const addQr = () => setS({ ...s, chat_quick_replies: [...qr, { question: "", answer: "" }] });
  const delQr = (i) => setS({ ...s, chat_quick_replies: qr.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        chat_enabled: s.chat_enabled,
        chat_hours: s.chat_hours,
        chat_welcome_message: s.chat_welcome_message,
        chat_offline_message: s.chat_offline_message,
        chat_quick_replies: (s.chat_quick_replies || []).filter((q) => q.question && q.answer),
      });
      toast.success("Chat-instellingen opgeslagen.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <div className="flex items-center justify-between">
          <div><div className="font-medium">Chat op de website</div><div className="text-sm text-ink-muted">Toon de chatwidget voor bezoekers.</div></div>
          <Switch data-testid="chat-enabled-switch" checked={s.chat_enabled} onCheckedChange={(v) => setS({ ...s, chat_enabled: v })} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <div className="flex items-center gap-2 mb-4"><Clock className="w-4 h-4 text-brand" /><h2 className="font-medium">Chattijden</h2></div>
        <p className="text-sm text-ink-muted mb-4">Binnen deze tijden ben je "online". Daarbuiten krijgt de bezoeker je offline-bericht.</p>
        <div className="space-y-2">
          {hours.map((h) => (
            <div key={h.day} className="flex items-center gap-3">
              <div className="w-28 text-sm">{DAYS[h.day]}</div>
              <Switch data-testid={`chat-day-${h.day}`} checked={h.enabled} onCheckedChange={(v) => setHour(h.day, { enabled: v })} />
              <Input type="time" value={h.start} onChange={(e) => setHour(h.day, { start: e.target.value })} disabled={!h.enabled} className="rounded-xl border-[#E8E2D9] w-28" />
              <span className="text-ink-muted text-sm">tot</span>
              <Input type="time" value={h.end} onChange={(e) => setHour(h.day, { end: e.target.value })} disabled={!h.enabled} className="rounded-xl border-[#E8E2D9] w-28" />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-4">
        <div><Label className="text-sm text-ink-muted mb-1.5 block">Welkomstbericht (online)</Label>
          <Textarea data-testid="chat-welcome" value={s.chat_welcome_message} onChange={(e) => setS({ ...s, chat_welcome_message: e.target.value })} rows={2} className="rounded-xl border-[#E8E2D9]" /></div>
        <div><Label className="text-sm text-ink-muted mb-1.5 block">Offline-bericht</Label>
          <Textarea data-testid="chat-offline" value={s.chat_offline_message} onChange={(e) => setS({ ...s, chat_offline_message: e.target.value })} rows={2} className="rounded-xl border-[#E8E2D9]" /></div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium">Snelle antwoorden</h2>
          <Button size="sm" variant="outline" onClick={addQr} data-testid="chat-add-qr" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Toevoegen</Button>
        </div>
        <p className="text-sm text-ink-muted mb-4">Veelgestelde vragen met een direct antwoord. Bezoekers kunnen erop tikken.</p>
        <div className="space-y-3">
          {qr.map((q, i) => (
            <div key={i} className="rounded-xl border border-[#F2EBE1] p-3 space-y-2" data-testid={`chat-qr-${i}`}>
              <div className="flex gap-2">
                <Input value={q.question} onChange={(e) => setQr(i, { question: e.target.value })} placeholder="Vraag" className="rounded-xl border-[#E8E2D9]" />
                <Button size="icon" variant="ghost" onClick={() => delQr(i)}><X className="w-4 h-4" /></Button>
              </div>
              <Textarea value={q.answer} onChange={(e) => setQr(i, { answer: e.target.value })} placeholder="Antwoord" rows={2} className="rounded-xl border-[#E8E2D9]" />
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} data-testid="chat-settings-save" className="rounded-full bg-brand hover:bg-brand-hover text-white">Opslaan</Button>
    </div>
  );
}
